import React, { FC, useEffect, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FormControl, Hidden, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { DocumentIcon, MessageIcon, RafterDownIcon } from "@/assets/icons";
import { Tab, TabPanel, Tabs } from "@/components/molecules";
import { mobileIntegration } from "@/integration";
import { isABroker } from "@/shared/hooks";

import {
    StyledActionItem,
    StyledActionList,
    StyledDetailsMobileWrapper,
    StyledDetailsPage,
    StyledDetailsSelectionInput,
    StyledDetailsSelectionMenuItem,
    StyledLeftSide,
    StyledPanelWrapper,
} from "./styled";

interface Panel {
    label: string;
    content: React.ReactElement;
    className?: string;
}

type DetailsPageProps = {
    panels?: Panel[];
    objectType: string;
    defaultSelectedTab?: number;
    card: React.ReactElement;
    content?: React.ReactElement;
    extraContent?: React.ReactElement;
    activities?: React.ReactElement;
    extraButtons?: React.ReactElement[];
    handleShowDocuments?: () => void;
    handleShowConversation?: () => void;
};

const DetailsPage: FC<DetailsPageProps> = ({
    card,
    content,
    panels,
    defaultSelectedTab = 0,
    activities,
    objectType,
    extraContent,
    extraButtons,
    handleShowDocuments,
    handleShowConversation,
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
    const [selectedDetail, setSelectedDetail] = useState(defaultSelectedTab);

    const handleSelectedDetailsOption = (event) => {
        setSelectedDetail(event.target.value);
    };

    useEffect(() => {
        setSelectedTab(defaultSelectedTab);
        setSelectedDetail(defaultSelectedTab);
    }, [defaultSelectedTab]);

    return (
        <StyledDetailsPage className={`${objectType}DetailsPage`}>
            <StyledLeftSide>
                {card}
                {((!mobileIntegration.isNativeApp() && handleShowDocuments) ||
                    (!mobileIntegration.isNativeApp() && handleShowConversation) ||
                    (extraButtons && extraButtons.length > 0)) && (
                    <StyledActionList>
                        {!mobileIntegration.isNativeApp() && handleShowDocuments && (
                            <StyledActionItem
                                $disabled={false}
                                onClick={handleShowDocuments}
                                startIcon={<DocumentIcon />}
                                data-testid={`${objectType}_detail_page-button-documents`}
                            >
                                {t("Common.Documents")}
                            </StyledActionItem>
                        )}
                        {!mobileIntegration.isNativeApp() && handleShowConversation && (
                            <StyledActionItem
                                $disabled={isABroker()}
                                onClick={!isABroker() && handleShowConversation}
                                disableRipple={isABroker()}
                                title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                                startIcon={<MessageIcon />}
                                data-testid={`${objectType}_detail_page-button-conversations`}
                            >
                                {t("Common.Messages")}
                            </StyledActionItem>
                        )}
                        {extraButtons?.map?.((button) => button)}
                    </StyledActionList>
                )}
                {!isSmallScreen && activities}
            </StyledLeftSide>
            <div style={{ flex: 2 }} className="rightSide">
                {content}
                {panels && panels.length > 0 && (
                    <Hidden smDown>
                        <Tabs value={selectedTab} onChange={(_e, tab) => setSelectedTab(tab)}>
                            {panels.map((tab, i) => (
                                <Tab
                                    id={`simple-tab-${i}`}
                                    aria-controls={`simple-tabpanel-${i}`}
                                    label={tab.label}
                                    data-testid={`${objectType}_detail_page-tab-${i}`}
                                    key={`desktop-tab-${tab.label}`}
                                />
                            ))}
                        </Tabs>
                        <StyledPanelWrapper className="panelWrapper">
                            {panels.map((tab, i) => (
                                <TabPanel
                                    key={`desktop-tabpanel-${tab.label}`}
                                    value={selectedTab}
                                    index={i}
                                    className={clsx("tabPanel", tab.className)}
                                >
                                    {tab.content}
                                </TabPanel>
                            ))}
                        </StyledPanelWrapper>
                    </Hidden>
                )}
                {panels && panels.length > 0 && (
                    <Hidden smUp>
                        <div>
                            {panels && panels.length > 1 && (
                                <FormControl>
                                    <StyledDetailsSelectionInput
                                        IconComponent={RafterDownIcon}
                                        classes={{ select: "select" }}
                                        defaultValue={defaultSelectedTab}
                                        fullWidth
                                        onChange={handleSelectedDetailsOption}
                                        MenuProps={{ className: "select-menu-list-without-padding" }}
                                    >
                                        {panels.map((tab, i) => (
                                            <StyledDetailsSelectionMenuItem
                                                classes={{ selected: "selected" }}
                                                key={`inputMenu${tab.label}`}
                                                value={i}
                                                data-testid={`option-inputMenu-${i}`}
                                            >
                                                {tab.label}
                                            </StyledDetailsSelectionMenuItem>
                                        ))}
                                    </StyledDetailsSelectionInput>
                                </FormControl>
                            )}

                            <StyledDetailsMobileWrapper className="detailsMobileWrapper">
                                {panels[selectedDetail].content}
                            </StyledDetailsMobileWrapper>
                        </div>
                    </Hidden>
                )}
                {extraContent}
                {isSmallScreen && activities}
            </div>
        </StyledDetailsPage>
    );
};

export default DetailsPage;
